import { configurationProfile } from 'repositories/configurationProfile';
import { device } from 'repositories/device';
import { Storage } from 'repositories/Storage';
import {
  Device,
  AvailableApps,
  ConfigurationProfileResult,
} from 'models/Device/type';
import {
  convertToDevice,
  convertToAvailableApps,
} from 'models/Device/transfer';
import { auth } from 'repositories/auth';
import { webclip } from 'repositories/webclip';
import { RepositoryError, InvalidParameterError } from 'repositories/Error';
import {
  ApplicationError,
  ConfigurationProfileInstallTokenMissingError,
} from 'models/Error';
import { Failure, Result, Success } from 'types';
import { ROUTER_PATH } from 'views/constants';
import { getRequestPath } from 'utils/string';
import { migration } from 'repositories/migration';

const IPHONE_REGISTRATION_TOKEN_EXPIRED_DATE = 15;
const CONFIGURATION_PROFILE_INSTALL_TOKEN_EXPIRED_DATE = 15;
// install token 欠落時にトークン再作成フローへリダイレクトして再試行する上限回数。
// 無限リトライを避けるため、この回数を超えた場合は方針Aのハンドリング済みエラーを返す。
const MAX_CONFIGURATION_PROFILE_INSTALL_TOKEN_RETRY = 3;
// リトライカウントの有効期限(分)。アクティブなリトライループ中のみ保持し、
// 時間が経てば自然にリセットされるようにするための短い期限。
const CONFIGURATION_PROFILE_INSTALL_TOKEN_RETRY_COUNT_EXPIRED_MINUTES = 15;

export class DeviceDomain {
  value?: Device;

  constructor(device?: Device) {
    this.value = device;
  }

  isInstalledConfigurationProfile(): boolean {
    const uid = this.value?.uid;
    return uid !== null && uid !== '' && uid !== undefined;
  }

  isAuthenticated(): boolean {
    return this.value?.user?.authorizationToken !== undefined;
  }

  hasIphoneRegistrationToken(): boolean {
    return this.value?.iphoneRegistrationToken !== undefined;
  }

  hasReInstall(): boolean {
    return this.value?.reInstall || false;
  }

  async availableApps(): Promise<Result<AvailableApps, ApplicationError>> {
    if (!this.isInstalledConfigurationProfile()) {
      return new Failure(
        new ApplicationError('required configuration profile')
      );
    }

    try {
      const availableAppsResponse = await device.availableApps();
      if (availableAppsResponse.error || !availableAppsResponse.results) {
        return new Failure(new ApplicationError());
      }
      return new Success(convertToAvailableApps(availableAppsResponse));
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  async registerEmail(
    email: string
  ): Promise<Result<undefined, ApplicationError>> {
    if (!this.isInstalledConfigurationProfile()) {
      return new Failure(
        new ApplicationError('required configuration profile')
      );
    }

    try {
      await device.registerEmail(email);
      return new Success(undefined);
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  async deleteEmail(): Promise<Result<undefined, ApplicationError>> {
    if (!this.isInstalledConfigurationProfile()) {
      return new Failure(
        new ApplicationError('required configuration profile')
      );
    }

    try {
      await device.deleteEmail();
      return new Success(undefined);
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  async logout(): Promise<Result<undefined, ApplicationError>> {
    if (!this.value?.user?.authorizationToken) {
      // authorization tokenがない場合はそのまま返す
      return new Success(undefined);
    }

    try {
      await auth.logout();
    } catch (e) {
      if (e instanceof RepositoryError && e.status === 401) {
        // セッション切れの場合はサーバサイドのセッションも既に無効なため、
        // ストレージをクリアしてログアウト完了とする
      } else {
        // 401 以外のエラーはサーバサイドのセッションが残っている可能性があるため、
        // ストレージをクリアせずエラーを返してユーザーにリトライを促す
        return ApplicationError.handleForRepositoryError(e);
      }
    }

    await Storage.delete('token');

    // これらの値が残っている場合があるので、ログアウト時に綺麗に消す
    await Storage.delete('iphone_registration_token');
    await Storage.delete('configuration_profile_install_token');
    await Storage.delete('configuration_profile_install_token_retry_count');
    await Storage.delete('configuration_profile_re_install');
    return new Success(undefined);
  }

  async installConfigurationProfileUrl(
    authorizationToken: string | undefined,
    iphoneRegistrationToken: string | undefined,
    distributionAccessKey: string,
    distributionKey: string,
    distributionDeviceName: string,
    appId: string,
    reInstall: boolean
  ): Promise<string | void> {
    if (this.isInstalledConfigurationProfile()) {
      return;
    }

    const currentSessionToken = await Storage.get(
      'configuration_profile_install_token'
    );
    if (currentSessionToken) {
      try {
        const result =
          await configurationProfile.checkProfileTokenValidation(
            currentSessionToken
          );
        if (result) {
          return configurationProfile.downloadUrl(currentSessionToken);
        }
      } catch (e) {
        ApplicationError.handleForRepositoryError(e);
      }

      await Storage.delete('configuration_profile_install_token');
    }

    try {
      const sessionToken = await DeviceDomain.createSessionToken(
        authorizationToken,
        iphoneRegistrationToken,
        distributionAccessKey,
        distributionKey,
        distributionDeviceName,
        appId,
        reInstall
      );
      await DeviceDomain.saveConfigurationProfileInstallToken(sessionToken);
      // トークン再作成に成功したのでリトライカウントをリセットする
      await Storage.delete('configuration_profile_install_token_retry_count');
      return configurationProfile.downloadUrl(sessionToken);
    } catch (e) {
      if (e instanceof InvalidParameterError) {
        return;
      }

      ApplicationError.handleForRepositoryError(e);
    }
  }

  /***************
   * static methods
   **************/

  static async startInstallConfigurationProfile(): Promise<
    Result<Device | null, ApplicationError>
  > {
    try {
      const sessionToken = await Storage.get(
        'configuration_profile_install_token'
      );
      if (!sessionToken) {
        // 方針B: トークン欠落時は plain Error を throw せず、
        // トークン再作成フローへリダイレクトさせるための専用エラーを返す。
        return await this.handleMissingConfigurationProfileInstallToken();
      }

      const configurationProfileResult =
        await this.checkConfigurationProfileInstalledWithSleep(sessionToken);

      await Storage.save('iphone_uid', configurationProfileResult.uid);
      if (configurationProfileResult.authorizationToken) {
        await Storage.save(
          'token',
          configurationProfileResult.authorizationToken
        );
      }

      await Storage.delete('iphone_registration_token');
      await Storage.delete('configuration_profile_install_token');
      await Storage.delete('configuration_profile_install_token_retry_count');
      await Storage.delete('configuration_profile_re_install');

      return new Success(await this.restore('configuration_profile_installer'));
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  // 方針B/A: install token 欠落時のハンドリング。
  // リトライ上限までは再作成フローへ誘導する専用エラー(方針B)を返し、
  // 上限に達した場合はハンドリング済みの ApplicationError(方針A)を返して Sentry への送出を防ぐ。
  private static async handleMissingConfigurationProfileInstallToken(): Promise<
    Result<Device | null, ApplicationError>
  > {
    const retryCount = Number(
      (await Storage.get('configuration_profile_install_token_retry_count')) ??
        '0'
    );

    if (retryCount < MAX_CONFIGURATION_PROFILE_INSTALL_TOKEN_RETRY) {
      // ユーザーが途中で離脱して後から再訪した際に古いカウントが残り続けないよう、
      // アクティブなリトライループ中のみ保持される短い有効期限を設定する
      const expiredDate = new Date();
      expiredDate.setMinutes(
        expiredDate.getMinutes() +
          CONFIGURATION_PROFILE_INSTALL_TOKEN_RETRY_COUNT_EXPIRED_MINUTES
      );
      const nextRetryCount = String(retryCount + 1);
      await Storage.save(
        'configuration_profile_install_token_retry_count',
        nextRetryCount,
        expiredDate
      );

      // localStorage が使用できない環境では Storage.save が no-op になり、
      // カウントが永続化されず方針Bのリロードが無限ループする。
      // 保存を確認できた場合のみ方針B(再作成フローへの再試行)を行い、
      // 確認できない場合は方針Aにフォールバックしてループを避ける。
      const savedRetryCount = await Storage.get(
        'configuration_profile_install_token_retry_count'
      );
      if (savedRetryCount === nextRetryCount) {
        return new Failure(new ConfigurationProfileInstallTokenMissingError());
      }
    }

    await Storage.delete('configuration_profile_install_token_retry_count');
    // apiErrorMessageLocaleId にマッピングを持たない生メッセージを渡すと
    // ダイアログで英字がそのまま表示されるため、一般エラー(general)にフォールバックする
    return new Failure(new ApplicationError());
  }

  static async login(
    email: string,
    password: string,
    invitationToken?: string
  ): Promise<Result<Device | null, ApplicationError>> {
    try {
      const response = await auth.login(email, password, invitationToken);
      if (response.results.authorization_token) {
        await Storage.save('token', response.results.authorization_token);
        return new Success(await this.restore());
      }

      await this.saveIphoneRegistrationToken(
        response.results.iphone_registration_token
      );
      return new Success({
        iphoneRegistrationToken: response.results.iphone_registration_token,
      });
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  static async signUp(
    transactionKey: string,
    password: string,
    name: string,
    locale: string,
    termsAccept: boolean,
    invitationToken?: string,
    oauthSessionToken?: string
  ): Promise<Result<Device | null, ApplicationError>> {
    try {
      let infoFromOAuth = undefined;
      if (oauthSessionToken) {
        infoFromOAuth = {
          iosSpaOnetimeToken: oauthSessionToken,
        };
      }
      const response = await auth.signUp(
        transactionKey,
        password,
        name,
        locale,
        termsAccept,
        invitationToken,
        infoFromOAuth
      );
      if (response.results.authorization_token) {
        await Storage.save('token', response.results.authorization_token);
        return new Success(await this.restore());
      }

      await this.saveIphoneRegistrationToken(
        response.results.iphone_registration_token
      );
      return new Success({
        iphoneRegistrationToken: response.results.iphone_registration_token,
      });
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  static async restoreFromWebclip(
    onetimeToken: string
  ): Promise<Result<[Device | null, string], ApplicationError>> {
    try {
      const webclipSessionResponse = await webclip.session(onetimeToken);

      const redirectPath = webclipSessionResponse.results
        .distribution_access_key
        ? getRequestPath(ROUTER_PATH.distribution, {
            distributionAccessKey:
              webclipSessionResponse.results.distribution_access_key,
          })
        : ROUTER_PATH.apps;

      await Storage.save(
        'token',
        webclipSessionResponse.results.authorization_token
      );
      await Storage.save(
        'iphone_uid',
        webclipSessionResponse.results.iphone_uid
      );
      return new Success([await this.restore(), redirectPath]);
    } catch (e) {
      if (e instanceof RepositoryError && e.status === 404) {
        const authorizationToken = await Storage.get('token');
        const uid = await Storage.get('iphone_uid');
        if (authorizationToken || uid) {
          return new Success([null, ROUTER_PATH.apps]);
        }

        return new Failure(new ApplicationError(e.apiMessage));
      }

      return ApplicationError.handleForRepositoryError(e);
    }
  }

  static async restoreFromMigration(
    onetimeToken: string
  ): Promise<Result<[Device | null, string], ApplicationError>> {
    try {
      const migrationSessionResponse = await migration.session(onetimeToken);

      const redirectPath = migrationSessionResponse.results.redirect_to;
      if (migrationSessionResponse.results.authorization_token) {
        await Storage.save(
          'token',
          migrationSessionResponse.results.authorization_token
        );
      }
      if (migrationSessionResponse.results.iphone_uid) {
        await Storage.save(
          'iphone_uid',
          migrationSessionResponse.results.iphone_uid
        );
      }
      if (migrationSessionResponse.results.iphone_registration_token) {
        await this.saveIphoneRegistrationToken(
          migrationSessionResponse.results.iphone_registration_token
        );

        // iphone registration tokenが渡された場合は
        // iphone registration tokenを使いConfigurationProfileを再インストールさせるため
        // iphone_uidは削除する
        const iphoneUid = await Storage.get('iphone_uid');
        if (iphoneUid) {
          await Storage.delete('iphone_uid');
        }
      }
      return new Success([await this.restore(), redirectPath]);
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  static async restore(context?: string): Promise<Device | null> {
    // CacheStorageによってprofile_tokenがwebclipからも見えてしまい、
    // 自分自身がwebclipなのか判断ができないので引数contextで制御する
    if (context === 'configuration_profile_installer') {
      await this.restoreFromConfigurationProfileInstallToken();
    }

    let authorizationToken = await Storage.get('token');
    const uid = await Storage.get('iphone_uid');
    let iphoneRegistrationToken =
      (await Storage.get('iphone_registration_token')) || undefined;
    let reInstall =
      (await Storage.get('configuration_profile_re_install')) || undefined;

    if (
      authorizationToken !== null &&
      uid === null &&
      iphoneRegistrationToken === undefined &&
      reInstall !== 'true'
    ) {
      // この状況は例外なのでauthorizationTokenを削除する
      await Storage.delete('token');
      authorizationToken = null;
    }

    const deviceFromToken =
      await this.restoreFromAuthorizationToken(authorizationToken);

    if (deviceFromToken === null) {
      await Storage.delete('token');

      // これらの値が残っている場合があるため、authorizationToken が保存されてたのにdeviceFromTokenが取れなかった（強制ログアウトされた）とき削除する
      if (authorizationToken !== null) {
        iphoneRegistrationToken = undefined;
        reInstall = undefined;
        await Storage.delete('iphone_registration_token');
        await Storage.delete('configuration_profile_install_token');
        await Storage.delete('configuration_profile_re_install');
      }
    }
    const deviceFromUid = await this.restoreFromUid(uid);
    if (deviceFromUid === null) {
      await Storage.delete('iphone_uid');
    }

    let resultDevice: Device | null = null;
    if (deviceFromToken !== null && deviceFromUid !== null) {
      resultDevice = {
        ...deviceFromUid,
        user: deviceFromToken.user,
      };
    } else if (deviceFromUid !== null) {
      resultDevice = deviceFromUid;
    } else if (deviceFromToken !== null) {
      resultDevice = deviceFromToken;
    }

    return {
      ...resultDevice,
      iphoneRegistrationToken,
      reInstall: reInstall === 'true',
    };
  }

  static async getEmail(): Promise<Result<string, ApplicationError>> {
    try {
      const emailResponse = await device.getEmail();
      return new Success(emailResponse.results.email);
    } catch (e) {
      return ApplicationError.handleForRepositoryError(e);
    }
  }

  static async emailActivation(
    activationCode: string
  ): Promise<Result<undefined, ApplicationError>> {
    try {
      await device.emailActivation(activationCode);
      return new Success(undefined);
    } catch (e) {
      // status: 400, 404のみ既知のエラーなのでApplicationErrorでハンドリングする
      return ApplicationError.handleForRepositoryError(e, [400, 404]);
    }
  }

  static async reInstallConfigurationProfile(): Promise<void> {
    await Storage.delete('iphone_uid');
    await Storage.save('configuration_profile_re_install', 'true');
  }

  static async saveIphoneRegistrationToken(token: string): Promise<void> {
    const expiredDate = new Date();
    expiredDate.setDate(
      new Date().getDate() + IPHONE_REGISTRATION_TOKEN_EXPIRED_DATE
    );
    await Storage.save('iphone_registration_token', token, expiredDate);
  }

  /***************
   * private methods
   **************/

  private static async restoreFromConfigurationProfileInstallToken() {
    const configurationProfileInstallToken = await Storage.get(
      'configuration_profile_install_token'
    );
    if (!configurationProfileInstallToken) {
      return;
    }

    try {
      const result = await this.checkConfigurationProfileInstalled(
        configurationProfileInstallToken
      );
      await Storage.save('iphone_uid', result.uid);
      if (result.authorizationToken) {
        await Storage.save('token', result.authorizationToken);
      }

      await Storage.delete('configuration_profile_install_token');
    } catch (e) {
      if (
        e instanceof RepositoryError &&
        e.status === 404 &&
        e.message === 'no session found'
      ) {
        await Storage.delete('configuration_profile_install_token');
      }
    }
  }

  private static async createSessionToken(
    authorizationToken: string | undefined,
    iphoneRegistrationToken: string | undefined,
    distributionAccessKey: string,
    distributionKey: string,
    distributionDeviceName: string,
    appId: string,
    reInstall: boolean
  ): Promise<string> {
    return await configurationProfile.createSessionToken({
      auth: {
        authorizationToken,
        iphoneRegistrationToken,
      },
      distribution: {
        accessKey: distributionAccessKey,
        passKey: distributionKey,
        deviceName: distributionDeviceName,
      },
      appId,
      reInstall,
    });
  }

  private static async checkConfigurationProfileInstalled(
    sessionToken: string
  ): Promise<ConfigurationProfileResult> {
    const response = await configurationProfile.checkRegistration(sessionToken);
    return {
      uid: response.results.iphone_uid,
      authorizationToken: response.results.authorization_token,
    };
  }

  private static async checkConfigurationProfileInstalledWithSleep(
    sessionToken: string
  ): Promise<ConfigurationProfileResult> {
    let result: ConfigurationProfileResult | null = null;
    do {
      try {
        result = await this.checkConfigurationProfileInstalled(sessionToken);
      } catch {
        await this.sleep(5);
      }
    } while (result === null);

    return result;
  }

  private static sleep(waitSeconds: number) {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, waitSeconds * 1000);
    });
  }

  private static async restoreFromAuthorizationToken(
    authorizationToken: string | null
  ): Promise<Device | null> {
    if (authorizationToken === null) {
      return null;
    }

    try {
      const infoResponse = await auth.info(authorizationToken);
      if (!infoResponse.results.user) {
        return null;
      }

      return {
        user: {
          authorizationToken: authorizationToken,
          name: infoResponse.results.user.name,
          profileIcon: infoResponse.results.user.profile_icon,
          locale: infoResponse.results.user.locale,
        },
      } as Device;
    } catch (e) {
      ApplicationError.handleForRepositoryError(e, [401]);

      return null;
    }
  }

  private static async restoreFromUid(
    uid: string | null
  ): Promise<Device | null> {
    if (uid === null) {
      return null;
    }

    try {
      const detailResponse = await device.detail(uid);
      return convertToDevice(uid, detailResponse);
    } catch (e) {
      ApplicationError.handleForRepositoryError(e, [401]);

      return null;
    }
  }

  private static async saveConfigurationProfileInstallToken(token: string) {
    const expiredDate = new Date();
    expiredDate.setDate(
      new Date().getDate() + CONFIGURATION_PROFILE_INSTALL_TOKEN_EXPIRED_DATE
    );
    await Storage.save(
      'configuration_profile_install_token',
      token,
      expiredDate
    );
  }
}
